import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Gate for protected (doc) routes. Renders children ONLY when the signed-in user
 * is authorized (staff or allowlisted, confirmed by /api/me). A signed-in but
 * not-authorized user is bounced to home, where the "not approved" screen
 * explains why. UX only — every protected request is enforced server-side.
 */
export default function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, isAuthorized, profileLoading, emailLinkState } = useAuth();
  const location = useLocation();

  const spinner = (label: string) => (
    <div className="flex min-h-screen items-center justify-center bg-paper-50 text-ink-500">
      <span>{label}</span>
    </div>
  );

  // Don't render gated content while a magic-link is completing (it may sign in
  // a different user).
  if (loading || emailLinkState === 'processing') return spinner('Loading…');
  // A cross-device email prompt belongs on home, where it's rendered.
  if (emailLinkState === 'needs_email') return <Navigate to="/" replace />;

  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  // Signed in — gate on AUTHORIZATION, not mere presence.
  if (profileLoading || isAuthorized === null) return spinner('Verifying access…');
  if (isAuthorized === false) {
    // Home renders the reason-aware "not approved" screen for signed-in users.
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
